import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Zap, CheckCircle2, Eye, EyeOff } from "lucide-react";

const PRODUCT_NAME = "Production Pay Tracker";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pwd !== pwd2) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-sky-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Smarter Pay. Stronger Teams.</p>
            <h1 className="text-xl font-bold font-display tracking-tight text-slate-900">{PRODUCT_NAME}</h1>
          </div>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-password-success">
            <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-700" />
            </div>
            <h2 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mb-2">Password updated</h2>
            <p className="text-slate-600">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mb-2">Set a new password</h2>
            <p className="text-slate-500 mb-8">Pick something you'll remember — at least 8 characters.</p>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="pwd" className="text-xs font-semibold uppercase tracking-wider text-slate-700">New password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="pwd"
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    minLength={8}
                    placeholder="••••••••"
                    className="h-12 pr-12"
                    autoComplete="new-password"
                    data-testid="reset-password-new-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-slate-500 hover:text-slate-900 focus:outline-none focus:text-sky-700"
                    data-testid="reset-password-toggle-btn"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="pwd2" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Confirm new password</Label>
                <Input
                  id="pwd2"
                  type={showPwd ? "text" : "password"}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  required
                  minLength={8}
                  placeholder="••••••••"
                  className="h-12 mt-1.5"
                  autoComplete="new-password"
                  data-testid="reset-password-confirm-input"
                />
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-bold bg-sky-600 hover:bg-sky-700"
                data-testid="reset-password-submit-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
              </Button>

              <p className="text-center text-sm text-slate-600 pt-2">
                <Link to="/login" className="font-semibold text-sky-700 hover:text-sky-800" data-testid="reset-password-back-link">
                  Back to sign in
                </Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
